import type { Campaign, Variant, VariantCampaign } from '~/modules/campaign/types';
import type { IThemePage } from '~/stores/themePage';
import type { TemplateType } from '~/types/template';

import { findVariantByOrigin } from '~/modules/campaign/utils';
import { parseTemplateInfoBy } from '~/utils';

export const findVariantByKey = <T extends Variant>(
  variants: T[] | undefined,
  key: string,
): T | undefined => {
  return variants?.find((variant) => {
    return variant?.templateSuffix === key;
  });
};

export const isVariantMatchedPageType = <T extends TemplateType>(
  variant: Variant | undefined,
  pageType: T,
): boolean => {
  if (!variant) return false;

  return variant.templateType === pageType;
};

export const isVariantSamePageType = (variants?: Variant[]): boolean => {
  if (!variants || variants.length === 0) return false;

  const uniqueTemplateTypes = new Set(variants.map((variant) => variant.templateType));

  return uniqueTemplateTypes.size === 1;
};

export const isVariantSameProviderSource = (
  variants?: Variant[],
  themePages?: IThemePage[],
): boolean => {
  if (!variants || variants.length === 0) return false;

  const uniqueTemplateTypes = new Set(
    variants.map((variant) => {
      const providerSource = parseTemplateInfoBy({
        templateSuffix: variant.templateSuffix,
        themePages,
      });
      return providerSource?.providerSource || 'N/A';
    }),
  );

  return uniqueTemplateTypes.size === 1;
};

export const isPickedTemplateOrigin = (campaign: Campaign | undefined): boolean => {
  if (!campaign) return false;

  const originVariant = findVariantByOrigin(campaign, true);
  return !!originVariant?.templateSuffix;
};

export const flatMapVariants = (campaigns?: Campaign[]): VariantCampaign[] | undefined => {
  if (!campaigns) return;

  return campaigns.flatMap(
    (campaign) =>
      campaign.variants?.flatMap((variant) => {
        if (!variant) return [];

        return {
          createdAt: variant.createdAt ?? '',
          deletedAt: variant.deletedAt ?? '',
          id: variant.id,
          isOrigin: variant.isOrigin ?? false,
          isWinner: variant.isWinner ?? false,
          templateSuffix: variant.templateSuffix ?? '',
          templateType: variant.templateType ?? '',
          trafficRate: variant.trafficRate ?? 0,
          updatedAt: variant.updatedAt ?? '',
          campaign: campaign,
        };
      }) ?? [],
  );
};
